import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { formatNumber, strToDateUTC } from '../../shared/utils';
import {
  ArmFixedTerm,
  FrequencyType,
  HousingExpense,
  HousingExpenseBreakdown,
  HousingExpenseCalcMethod,
  HousingExpenseType,
  LoanPurpose,
  LoanTerm,
  MortgageType,
  OtherHousingExpenses,
  Predicate,
  PricingResult,
  ProductType,
  PropertyTaxesHousingExpenses,
  PropertyType,
  ScenarioRow,
  SupplementalPropertyInsuranceHousingExpenses,
  VaFundingFeeType,
  armFixedTermLabel,
  columnInfo,
  loanTermLabel,
  mortgageTypeLabel,
  productTypeLabel,
} from '../models';

export const calculateUpfrontMiFactorForVA = (
  loanPurpose: LoanPurpose,
  fundingFeeType: VaFundingFeeType,
  salesPrice: number,
  baseLoan: number,
  exempt: boolean,
  propertyType: PropertyType,
): number => {
  // https://docs.google.com/spreadsheets/d/1aRVAOZHdMRVIDEcB_gymEaJabGrp_odp8GxoUV1qCM8/edit#gid=1536647983
  if (!salesPrice || !baseLoan || exempt) {
    return 0;
  }

  if (loanPurpose === LoanPurpose.VARateReduction) {
    return 0.5;
  }

  if (propertyType === PropertyType.ManufacturedSingle || propertyType === PropertyType.ManufacturedDouble) {
    return 1.0;
  }

  const isRefinanceType = isRefinance(loanPurpose);
  let upfrontMiFactor = 0;

  if (isRefinanceType) {
    if (fundingFeeType === VaFundingFeeType.RegularMilitaryFirstUse || fundingFeeType === VaFundingFeeType.ReservesOrGuardFirstUse) {
      upfrontMiFactor = 2.15;
    } else if (fundingFeeType === VaFundingFeeType.RegularMilitarySubsequentUse || fundingFeeType === VaFundingFeeType.ReservesOrGuardSubsequentUse) {
      upfrontMiFactor = 3.3;
    }
  } else {
    const downPaymentPercentage = formatNumber((1 - baseLoan / salesPrice) * 100, 3);
    if (downPaymentPercentage < 5.0) {
      if (fundingFeeType === VaFundingFeeType.RegularMilitaryFirstUse || fundingFeeType === VaFundingFeeType.ReservesOrGuardFirstUse) {
        upfrontMiFactor = 2.15;
      } else if (
        fundingFeeType === VaFundingFeeType.RegularMilitarySubsequentUse ||
        fundingFeeType === VaFundingFeeType.ReservesOrGuardSubsequentUse
      ) {
        upfrontMiFactor = 3.3;
      }
    } else if (downPaymentPercentage >= 5.0 && downPaymentPercentage < 10.0) {
      if (
        fundingFeeType === VaFundingFeeType.RegularMilitaryFirstUse ||
        fundingFeeType === VaFundingFeeType.ReservesOrGuardFirstUse ||
        fundingFeeType === VaFundingFeeType.RegularMilitarySubsequentUse ||
        fundingFeeType === VaFundingFeeType.ReservesOrGuardSubsequentUse
      ) {
        upfrontMiFactor = 1.5;
      }
    } else if (downPaymentPercentage >= 10.0) {
      if (
        fundingFeeType === VaFundingFeeType.RegularMilitaryFirstUse ||
        fundingFeeType === VaFundingFeeType.ReservesOrGuardFirstUse ||
        fundingFeeType === VaFundingFeeType.RegularMilitarySubsequentUse ||
        fundingFeeType === VaFundingFeeType.ReservesOrGuardSubsequentUse
      ) {
        upfrontMiFactor = 1.25;
      }
    }
  }
  return upfrontMiFactor;
};

export const isRefinance = (loanPurpose: LoanPurpose) => {
  switch (loanPurpose) {
    case LoanPurpose.RefiCashout:
    case LoanPurpose.RefiRateTermLimitedCO:
    case LoanPurpose.FHAStreamlineRefi:
    case LoanPurpose.VARateReduction:
    case LoanPurpose.SimpleRefinance: {
      return true;
    }
  }
  return false;
};

export const mapMortgageTypes = (value?: MortgageType[]) => {
  return value?.map((v) => mortgageTypeLabel[v]).join(', ');
};

export const mapLoanTerms = (value?: LoanTerm[]): string => {
  return value?.map((v) => loanTermLabel[v]).join(', ');
};

export const mapArmFixedTerms = (value?: ArmFixedTerm[]): string => {
  return value?.map((v) => armFixedTermLabel[v]).join(', ');
};

export const mapProductTypes = (value?: ProductType[]): string => {
  return value?.map((v) => productTypeLabel[v]).join(', ');
};

export const parseScenarioResponseDateType = (scenarioRow: ScenarioRow[]) => {
  scenarioRow.forEach((scenario) => {
    Object.keys(scenario).forEach((key) => {
      if (columnInfo[key]?.type === 'date' && typeof scenario[key] === 'string') {
        scenario[key] = columnInfo[key].label === 'Last Priced' ? new Date(scenario[key]) : strToDateUTC(scenario[key]);
      }
    });
  });
};

export const getCurrencyString = (value: string): string => {
  return `$${Number(value).toLocaleString('en-US')}`;
};

export const getDateString = (value: string): string => {
  const dateString = (value as unknown as Date).toISOString();
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

export const getPercentageString = (value: number | string, numDecimals = 0): string => {
  if (!value) {
    return '';
  }

  let percentageString = value.toString();

  try {
    const decimalIndex = percentageString.indexOf('.');

    if (decimalIndex === -1) {
      percentageString += '.';
      for (let i = 0; i < numDecimals; i++) {
        percentageString += '0';
      }
    } else {
      const length = percentageString.length;
      const currentDecimalCount = length - (decimalIndex + 1);
      if (currentDecimalCount !== numDecimals) {
        const decimalsToAdd = numDecimals - currentDecimalCount;
        for (let i = 0; i < decimalsToAdd; i++) {
          percentageString += '0';
        }
      }
    }

    percentageString += '%';
  } catch {
    percentageString = '';
  }

  return percentageString;
};

export const getPriceString = (value: string): string => {
  const numberFormat = new Intl.NumberFormat('en-US', {
    minimumIntegerDigits: 1,
    minimumFractionDigits: 3,
  });
  return numberFormat.format(Number.parseFloat(value));
};

export const round2 = (n: number): number => {
  if (!Number.isFinite(n)) {
    return n;
  }

  const neg = n < 0 ? -1 : 1;

  // Used toFixed(3) here to make sure we always have exactly 3 decimal digits (no "NaN", no "1e-7")
  const s = Math.abs(n).toFixed(3);
  const [i, d = '000'] = s.split('.');

  let cents = parseInt(d.slice(0, 2), 10);
  if (d[2] >= '5') {
    cents += 1;
  }

  let intPart = BigInt(i);
  if (cents === 100) {
    intPart += 1n;
    cents = 0;
  }

  return neg * Number(`${intPart.toString()}.${cents.toString().padStart(2, '0')}`);
};

export const toMMDDYYYY = (date?: Date, useUTC = true): string => {
  if (!date) {
    return '';
  }

  const d = date instanceof Date ? date : typeof date === 'string' ? new Date(date) : null;

  if (!d || Number.isNaN(d.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: useUTC ? 'UTC' : undefined,
  }).format(d);
};

export const arithmeticBasedOnFrequency = (value: number, frequency: FrequencyType): number => {
  switch (frequency) {
    case FrequencyType.Annually:
      return value / 12;
    case FrequencyType.SemiAnnually:
      return value / 6;
    case FrequencyType.Quarterly:
      return value / 3;
    default:
      return value;
  }
};

export const totalPiti = (nums: number[]): number => {
  let total = 0;
  for (const n of nums) {
    total += round2(n);
  }
  return total;
};

export const calculateHousingExpenseBreakdown = (housingExpenses?: HousingExpense[] | null): HousingExpenseBreakdown => {
  const result: HousingExpenseBreakdown = {
    supplementalPropertyTaxTotal: 0,
    propertyTaxTotal: 0,
    otherTotal: 0,
  };

  if (!housingExpenses?.length) {
    return result;
  }

  housingExpenses.forEach((expense) => {
    const value = arithmeticBasedOnFrequency(
      expense.amount,
      expense.calcMethod === HousingExpenseCalcMethod.Factor ? FrequencyType.Annually : expense.frequency,
    );

    if (SupplementalPropertyInsuranceHousingExpenses.includes(expense.type)) {
      result.supplementalPropertyTaxTotal += value;
    } else if (PropertyTaxesHousingExpenses.includes(expense.type)) {
      result.propertyTaxTotal += value;
    } else if (OtherHousingExpenses.includes(expense.type)) {
      result.otherTotal += value;
    } else if (expense.type === HousingExpenseType.HomeownersInsurance && expense.amount > 0) {
      result.homeownersInsurance = value;
    } else if (expense.type === HousingExpenseType.Hoa && expense.amount > 0) {
      result.hoaDues = value;
    }
  });

  return result;
};

export const omitByPredicate = <T extends object>(obj: T, predicate: Predicate<T>): Partial<T> => {
  const result: Partial<T> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (!predicate(value, key)) {
        result[key] = value;
      }
    }
  }
  return result;
};

export const isSameRateRow = (a: PricingResult, b: PricingResult): boolean => a.lockPeriod === b.lockPeriod && a.interestRate === b.interestRate;

export const requiredTrimmed: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;

  if (!value) {
    return { required: true };
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return { required: true };
  }

  return null;
};
